import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { coreTeamGroups, opportunityPaths } from "../../data/content.js";
import { RevealItem, Rise, mailto } from "./shared.jsx";
import "./join.css";

const GROUPS = coreTeamGroups.filter((group) => group.label !== "Founding board");

const INCLUDE = [
  ["Your name", "So we know who is writing."],
  ["The area you want to work in", "Pick a group above, or say you are not sure."],
  ["A short example of relevant work", "A link, a photo or two lines describing it."],
  ["Your weekly availability", "Be realistic about the hours you have."],
  ["What you want to learn", "Previous experience is useful context. Say what you want to learn too."],
];

function GroupPicker({ picked, setPicked }) {
  const reduce = useReducedMotion();
  const group = GROUPS[picked];
  return (
    <div className="jn-tm-pick">
      <div className="jn-tm-pick__list" role="radiogroup" aria-label="Working groups">
        {GROUPS.map((item, index) => (
          <button
            key={item.label}
            type="button"
            role="radio"
            aria-checked={picked === index}
            className={`jn-tm-pick__opt ${picked === index ? "is-on" : ""}`}
            onClick={() => setPicked(index)}
          >
            {picked === index && (
              <motion.span className="jn-tm-pick__fill" layoutId="jn-tm-fill" transition={{ duration: reduce ? 0 : 0.5, ease: EASE_OUT }} />
            )}
            <span className="jn-tm-pick__label">{item.label}</span>
            <small>{item.members.length} people</small>
          </button>
        ))}
      </div>
      <div className="jn-tm-pick__panel" aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={group.label}
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
          >
            <p className="jn-mono">You would work alongside</p>
            <ul>
              {group.members.map((member) => (
                <li key={member.name}>
                  <span className="jn-tm-pick__initials" aria-hidden="true">{member.name.split(" ").map((part) => part[0]).join("")}</span>
                  <strong>{member.name}</strong>
                  <span>{member.role}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function JoinTeamPage() {
  const selected = opportunityPaths.find((item) => item.slug === "team");
  const [picked, setPicked] = useState(0);
  const groupLabel = GROUPS[picked].label;
  const email = mailto(`${selected.emailSubject}: ${groupLabel}`);

  return (
    <PageFrame className="rd-page jn-page jn-tm">
      <Seo title={`${selected.title} | Project GRID`} path={`/student-opportunities/${selected.slug}`} description={selected.body} />
      <main>
        <header className="jn-wrap jn-tm-hero">
          <div className="jn-tm-hero__copy">
            <Rise as="p" className="jn-mono" delay={0.05}>Join the team</Rise>
            <MaskWords as="h1" className="jn-h1 jn-tm-hero__title" text="Own a real piece of the work." onMount delay={0.1} />
            <Rise as="p" className="jn-lede" delay={0.4}>
              {selected.promise} {selected.body}
            </Rise>
            <Rise className="gh-actions" delay={0.5}>
              <a className="gh-btn gh-btn--fill" href="#jn-tm-groups"><span className="roll" data-label="Choose a working group"><span>Choose a working group</span></span></a>
            </Rise>
          </div>
          <ClipPhoto
            className="jn-tm-hero__photo"
            src="/images/grid-photo-06-p7.jpg"
            alt="GRID co-founders Moksh Sindhwani and Tanishq Aggarwal at Metamorphosis"
            caption="Co-founders Moksh Sindhwani and Tanishq Aggarwal. GRID started as two students."
            sizes="(max-width: 999px) 100vw, 40vw"
            priority
          />
        </header>

        <section className="jn-wrap jn-tm-groups" id="jn-tm-groups" aria-labelledby="jn-tm-groups-title">
          <header className="jn-tm-groups__head">
            <MaskWords id="jn-tm-groups-title" className="jn-h2" text="Choose where you can contribute." />
            <p className="jn-lede">These are the working groups that run GRID today, with the people in them now. Your enquiry should name the kind of work you want to do.</p>
          </header>
          <GroupPicker picked={picked} setPicked={setPicked} />
        </section>

        <section className="jn-wrap jn-tm-do" aria-labelledby="jn-tm-do-title">
          <MaskWords id="jn-tm-do-title" className="jn-h2" text="What you would do." />
          <ol>
            {selected.steps.map((step, index) => (
              <RevealItem key={step} delay={index * 0.07}>
                <span className="jn-tm-do__num">{index + 1}</span>
                <strong>{step}</strong>
              </RevealItem>
            ))}
          </ol>
        </section>

        <section className="jn-wrap jn-tm-apply" aria-labelledby="jn-tm-apply-title">
          <div className="jn-tm-apply__card">
            <h2 id="jn-tm-apply-title" className="jn-h3">How to apply</h2>
            <p>Email a short introduction. The subject is filled in for the group you picked above.</p>
            <ul>
              {INCLUDE.map(([title, note]) => (
                <li key={title}><strong>{title}</strong> <span>{note}</span></li>
              ))}
            </ul>
            <a className="gh-btn gh-btn--fill" href={email}>
              Email a team enquiry <ArrowUpRight weight="bold" aria-hidden="true" />
            </a>
            <p className="jn-small">Subject: {selected.emailSubject}: {groupLabel}</p>
          </div>
          <div className="jn-tm-apply__note">
            <p><strong>An enquiry is not an offer of a role.</strong> We will need to discuss the scope and availability of a role before confirming it.</p>
            <Link className="gh-link" to="/team">Meet the current team <ArrowRight weight="bold" aria-hidden="true" /></Link>
            <Link className="gh-link" to="/student-opportunities/internships">Looking for an internship instead <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
